import { ActionTokenData } from '../entities/action.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getActionTokenDataByIDs = registerQuery({
  operationName: 'getActionTokenDataByIDs',
  type: () => [ActionTokenData],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ActionTokenData>>(gqlFields: SelSet) =>
      readEntitiesByIDs(ActionTokenData, ids, gqlFields),
});

export const subscribeActionTokenDataByIDs = registerSubscription({
  operationName: 'subscribeActionTokenDataByIDs',
  type: () => [ActionTokenData],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ActionTokenData>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(ActionTokenData, ids, gqlFields),
});

export const getAllActionTokenData = registerQuery({
  operationName: 'getAllActionTokenData',
  type: () => [ActionTokenData],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ActionTokenData>>(gqlFields: SelSet) =>
      readAllEntities(ActionTokenData, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllActionTokenData = registerSubscription({
  operationName: 'subscribeAllActionTokenData',
  type: () => [ActionTokenData],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ActionTokenData>>(gqlFields: SelSet) =>
      watchAllEntities(ActionTokenData, gqlFields, { skip, limit, excludeIDs }),
});

export const getActionTokenDataByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<ActionTokenData>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getActionTokenDataByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeActionTokenDataByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<ActionTokenData>>(gqlFields: SelSet) =>
    id ? subscribeActionTokenDataByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
